/**
 * Admin → Team: the tenant's admins. Every admin has the same access; each is Active or Inactive, and only active
 * admins count toward the limit Super Admin sets for the tenant (see backend/tenant_admins.py).
 *
 * The server owns every rule shown here — the limit, duplicate numbers, who may be deactivated — so an error from the
 * API is shown as-is.
 */
import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Power, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const fullName = (a) => `${a.first_name || ""} ${a.last_name || ""}`.trim() || "—";

function StatusBadge({ status }) {
    const active = status === "ACTIVE";
    return (
        <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${active ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`}>
            {active ? "Active" : "Inactive"}
        </span>
    );
}

function Who({ a }) {
    return (
        <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="font-semibold text-sm break-words">{fullName(a)}</span>
                {a.is_you && <span className="text-[10px] font-bold uppercase tracking-wider text-primary">You</span>}
                {a.is_primary && <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Primary</span>}
            </div>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">+91 {a.mobile_number}</div>
            {a.email && <div className="text-xs text-muted-foreground break-all">{a.email}</div>}
        </div>
    );
}

export default function AdminTeam() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);       // {mode: "add"|"edit", admin?}
    const [confirm, setConfirm] = useState(null); // admin whose status is being changed
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/admin/team");
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load your admins.");
        }
    };
    useEffect(() => { load(); }, []);

    const full = !!data && data.active >= data.limit;

    const save = async (values) => {
        setBusy(true);
        try {
            if (form.mode === "add") {
                await api.post("/admin/team", values);
                toast.success(`Admin added. They sign in at ${window.location.origin}/admin with this mobile number and an OTP.`, { duration: 8000 });
            } else {
                const { mobile_number, ...rest } = values;   // the number is the login; it can't be edited
                await api.patch(`/admin/team/${form.admin.id}`, rest);
                toast.success("Saved.");
            }
            setForm(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const changeStatus = async () => {
        const a = confirm;
        const next = a.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        setBusy(true);
        try {
            await api.patch(`/admin/team/${a.id}`, { status: next });
            toast.success(next === "ACTIVE" ? `${fullName(a)} is active and can sign in.` : `${fullName(a)} is inactive and signed out.`);
            setConfirm(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't change the status. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const actions = (a, prefix) => (
        <div className={`flex gap-2 ${prefix === "team-" ? "flex-nowrap justify-end" : "flex-wrap"}`}>
            <button
                type="button"
                data-testid={`${prefix}edit-${a.id}`}
                onClick={() => setForm({ mode: "edit", admin: a })}
                className="touch-target inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted"
            >
                <Pencil size={12} /> Edit
            </button>
            {!a.is_you && (
                <button
                    type="button"
                    data-testid={`${prefix}toggle-${a.id}`}
                    onClick={() => setConfirm(a)}
                    disabled={a.status !== "ACTIVE" && full}
                    title={a.status !== "ACTIVE" && full ? "All active admin places are in use" : undefined}
                    className={`touch-target inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${a.status === "ACTIVE" ? "border-destructive/40 text-destructive hover:bg-destructive/5" : "border-emerald-600/40 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400"}`}
                >
                    <Power size={12} /> {a.status === "ACTIVE" ? "Deactivate" : "Activate"}
                </button>
            )}
        </div>
    );

    if (!data) {
        return <div className="py-16 text-center text-muted-foreground" data-testid="team-loading"><Loader2 size={18} className="inline animate-spin" /></div>;
    }

    return (
        <div data-testid="team-page" className="min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold">Team</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-prose">Admins who can use this console. Every admin has the same access.</p>
                </div>
                <button
                    type="button"
                    data-testid="team-add-admin"
                    disabled={full}
                    onClick={() => setForm({ mode: "add" })}
                    className="touch-target w-full sm:w-auto inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-bold disabled:opacity-50"
                >
                    <Plus size={14} /> Add Admin
                </button>
            </div>

            <div className="mt-4 bg-card border border-border rounded-2xl p-4" data-testid="team-usage">
                <p className="text-sm">
                    <span className="text-muted-foreground">Active Admins: </span>
                    <span className="text-xl font-bold tabular-nums" data-testid="team-usage-count">{data.active} / {data.limit}</span>
                </p>
                <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden" aria-hidden="true">
                    <div className={`h-full rounded-full ${full ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${Math.min(100, (data.active / Math.max(1, data.limit)) * 100)}%` }} />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Inactive admins don't count toward the limit.</p>
                {full && (
                    <p className="mt-2 text-xs text-amber-800 dark:text-amber-300" data-testid="team-limit-full">
                        All active admin places are in use. Deactivate an admin to free a place, or ask the platform to raise the limit.
                    </p>
                )}
            </div>

            {!data.admins.some((a) => a.is_you) && (
                <p className="mt-4 text-xs text-muted-foreground" data-testid="team-viewer-not-listed">
                    You're signed in with a platform support account, so you aren't listed here or counted in the limit.
                </p>
            )}

            {data.admins.length === 0 ? (
                <div className="mt-4 bg-card border border-dashed border-border rounded-2xl px-4 py-10 text-center" data-testid="team-empty">
                    <UserRound size={28} className="mx-auto text-muted-foreground opacity-60" />
                    <p className="mt-2 font-semibold">No admins yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Add the people who should be able to sign in to this console.</p>
                </div>
            ) : (
                <div className="mt-4 bg-card border border-border rounded-2xl overflow-hidden">
                    {/* Phones and tablets in portrait */}
                    <div className="lg:hidden">
                        {data.admins.map((a) => (
                            <div key={a.id} data-testid={`team-card-${a.id}`} className="border-t border-border first:border-t-0 px-4 py-3">
                                <div className="flex items-start justify-between gap-2">
                                    <Who a={a} />
                                    <div className="shrink-0"><StatusBadge status={a.status} /></div>
                                </div>
                                <div className="mt-2">{actions(a, "team-card-")}</div>
                            </div>
                        ))}
                    </div>
                    {/* Tablets in landscape and desktop */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                                    <th className="px-4 py-3">Admin</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.admins.map((a) => (
                                    <tr key={a.id} data-testid={`team-row-${a.id}`} className="border-t border-border align-top">
                                        <td className="px-4 py-3"><Who a={a} /></td>
                                        <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                                        <td className="px-4 py-3 w-px whitespace-nowrap">{actions(a, "team-")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {form && <AdminForm form={form} busy={busy} onClose={() => setForm(null)} onSubmit={save} />}
            {confirm && <ConfirmStatus admin={confirm} busy={busy} onCancel={() => setConfirm(null)} onConfirm={changeStatus} />}
        </div>
    );
}

// Bottom sheet on phones, centred dialog from sm up. The body scrolls; the actions stay visible above the home
// indicator / gesture bar.
function Sheet({ title, onClose, children, footer, testid }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:px-4" onClick={onClose} data-testid={testid}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="bg-background w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[88vh]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-2">
                    <h3 className="font-bold text-lg break-words min-w-0">{title}</h3>
                    <button type="button" onClick={onClose} aria-label="Close" className="touch-target shrink-0 inline-flex items-center justify-center rounded-lg hover:bg-muted"><X size={18} /></button>
                </div>
                <div className="px-5 overflow-y-auto">{children}</div>
                <div className="px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-border mt-3">{footer}</div>
            </div>
        </div>
    );
}

function AdminForm({ form, busy, onClose, onSubmit }) {
    const editing = form.mode === "edit";
    const a = form.admin || {};
    const [values, setValues] = useState({
        first_name: a.first_name || "",
        last_name: a.last_name || "",
        mobile_number: a.mobile_number || "",
        email: a.email || "",
    });
    const set = (k) => (e) => setValues({ ...values, [k]: e.target.value });
    const canSubmit = values.first_name.trim() && (editing || values.mobile_number.replace(/\D/g, "").length >= 10);
    const input = "w-full rounded-xl bg-muted px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary";
    const label = "block text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1";

    return (
        <Sheet
            testid="team-form"
            title={editing ? "Edit Admin" : "Add Admin"}
            onClose={onClose}
            footer={
                <button
                    type="button"
                    data-testid="team-form-submit"
                    disabled={busy || !canSubmit}
                    onClick={() => onSubmit({ ...values, first_name: values.first_name.trim() })}
                    className="w-full touch-target bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    {busy && <Loader2 size={14} className="animate-spin" />} {editing ? "Save" : "Add Admin"}
                </button>
            }
        >
            <div className="grid gap-3 text-sm">
                {!editing && <p className="text-xs text-muted-foreground">They'll have the same access as you.</p>}
                <label className="block">
                    <span className={label}>First name</span>
                    <input data-testid="team-form-first-name" className={input} value={values.first_name} onChange={set("first_name")} autoComplete="given-name" />
                </label>
                <label className="block">
                    <span className={label}>Last name <span className="normal-case font-normal">(optional)</span></span>
                    <input data-testid="team-form-last-name" className={input} value={values.last_name} onChange={set("last_name")} autoComplete="family-name" />
                </label>
                <label className="block">
                    <span className={label}>Mobile number</span>
                    <input
                        data-testid="team-form-mobile"
                        className={`${input} disabled:opacity-60 font-mono`}
                        value={values.mobile_number}
                        onChange={set("mobile_number")}
                        disabled={editing}
                        inputMode="numeric"
                        autoComplete="tel-national"
                        placeholder="10-digit mobile"
                    />
                    <span className="block text-[11px] text-muted-foreground mt-1">
                        {editing ? "The mobile number is their login and can't be changed. Add a new admin instead." : "They sign in with this number and an OTP. No password needed."}
                    </span>
                </label>
                <label className="block">
                    <span className={label}>Email <span className="normal-case font-normal">(optional)</span></span>
                    <input data-testid="team-form-email" type="email" className={input} value={values.email} onChange={set("email")} autoComplete="email" />
                </label>
            </div>
        </Sheet>
    );
}

function ConfirmStatus({ admin, busy, onCancel, onConfirm }) {
    const deactivating = admin.status === "ACTIVE";
    return (
        <Sheet
            testid="team-confirm"
            title={deactivating ? `Deactivate ${fullName(admin)}?` : `Activate ${fullName(admin)}?`}
            onClose={onCancel}
            footer={
                <div className="grid grid-cols-2 gap-2">
                    <button type="button" data-testid="team-confirm-cancel" onClick={onCancel} className="touch-target rounded-xl border border-border py-3 font-semibold">Cancel</button>
                    <button
                        type="button"
                        data-testid="team-confirm-yes"
                        disabled={busy}
                        onClick={onConfirm}
                        className={`touch-target rounded-xl py-3 font-bold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 ${deactivating ? "bg-destructive" : "bg-emerald-600"}`}
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />} {deactivating ? "Deactivate" : "Activate"}
                    </button>
                </div>
            }
        >
            <p className="text-sm text-muted-foreground">
                {deactivating
                    ? "They'll be signed out straight away, can't sign in again, and stop getting order notifications. Their active admin place is freed."
                    : "They'll be able to sign in to this console again with their mobile number and an OTP. This uses one active admin place."}
            </p>
        </Sheet>
    );
}
